import React, { useRef, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

const PinInputWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin-right: 10px;
`;

const OtpInput = styled.input`
    width: 3.5rem;
    height: 3.5rem;
    margin: 0.5rem;
    text-align: center;
    font-size: 1.5rem;
    border-radius: 6px;
    border-width: 1px;
    border: ${({ otpError, theme }) => (otpError ? `1px solid ${theme.dangerMain}` : '')};
    box-shadow: ${({ otpError, theme }) => (otpError ? `0 0 0 2px ${theme.dangerLight}` : 'none')};
    outline: none;
`;

interface IPinInputProps {
    otp: string[];
    setOtp: Dispatch<SetStateAction<string[]>>;
    otpError: string;
    setOtpError: Dispatch<SetStateAction<string>>;
}

/**
 * PinInput Component
 * -------------------
 * Renders an OTP input field for multi-digit entry.
 *
 * Features:
 * - Auto-focuses next input on entry.
 * - Moves focus back on `Backspace`.
 * - Supports pasting OTP.
 * - Shows error state via `otpError`.
 *
 * Props:
 * - `otp`: OTP digits array.
 * - `setOtp`: Updates OTP state.
 * - `otpError`: Validation error message.
 * - `setOtpError`: Updates error state.
 */

const PinInput: React.FC<IPinInputProps> = ({ otp, setOtp, otpError, setOtpError }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (index, e) => {
        const value = e.target.value.replace(/\D/g, '');

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < otp.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        setOtpError('');
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus(); // Move focus to the previous input
            const newOtp = [...otp];
            newOtp[index] = '';
            setOtp(newOtp);
        }
    };

    const handleClick = index => {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.setSelectionRange(0, 1);
    };

    const handlePaste = e => {
        setOtpError('');
        const pasteData = e.clipboardData.getData('text');
        const pastedDigits = pasteData.replace(/\D/g, '').split('');
        const newOtp = [...otp];
        if (pastedDigits.length <= otp.length) {
            for (let i = 0; i < pastedDigits.length; i++) {
                newOtp[i] = pastedDigits[i];
            }

            // Clear any remaining fields
            for (let i = pastedDigits.length; i < otp.length; i++) {
                newOtp[i] = '';
            }

            setOtp(newOtp);
            // Move the focus to the first empty input or last filled input
            inputRefs.current[pastedDigits.length - 1]?.focus();
        } else {
            const newOtp = pastedDigits.slice(0, otp.length);
            setOtp(newOtp);
            inputRefs.current[otp.length - 1]?.focus();
        }
    };

    return (
        <PinInputWrapper className="gi-modal__input">
            {otp.map((value, index) => (
                <OtpInput
                    key={index}
                    ref={input => (inputRefs.current[index] = input)}
                    id={`otp${index}`}
                    data-testid={`otp${index}`}
                    value={value}
                    onChange={e => handleChange(index, e)}
                    onClick={() => handleClick(index)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    maxLength={1}
                    otpError={otpError}
                />
            ))}
        </PinInputWrapper>
    );
};

export default PinInput;
